<template>
  <v-card class="org-notes">
    <v-card-text>
      <v-textarea
        v-model="draft"
        label="Escribe una nota sobre la empresa…"
        :counter="MAX_LENGTH"
        :maxlength="MAX_LENGTH"
        :disabled="saving"
        auto-grow
        rows="3"
        outlined
        hide-details="auto"
      />
      <div class="d-flex align-center mt-2">
        <v-alert v-if="saveError" type="error" dense text class="mb-0 mr-3 flex-grow-1">{{ saveError }}</v-alert>
        <v-spacer />
        <v-btn color="primary" depressed :loading="saving" :disabled="!canSave" @click="save">Guardar nota</v-btn>
      </div>
    </v-card-text>

    <v-divider />

    <v-card-text>
      <div v-if="loading" class="d-flex justify-center py-6">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <div v-else-if="loadError" class="d-flex flex-wrap align-center">
        <v-alert type="warning" text dense class="mb-0 mr-3">{{ loadError }}</v-alert>
        <v-btn text color="primary" @click="load">Reintentar</v-btn>
      </div>

      <template v-else>
        <p v-if="!notes.length" class="org-notes__empty mb-0">Todavía no hay notas de esta empresa.</p>

        <div v-for="note in notes" :key="note.id" class="org-notes__note">
          <div class="d-flex align-center">
            <div class="org-notes__meta">
              <strong>{{ note.author }}</strong> · {{ formatDate(note.createdAt) }}
              <span v-if="note.editedBy" class="org-notes__edited">· Editada por {{ note.editedBy }}</span>
            </div>
            <v-spacer />
            <v-btn
              v-if="note.fromAdmin && editingId !== note.id"
              small
              text
              :disabled="editingId !== null"
              @click="startEdit(note)"
            >
              Editar
            </v-btn>
          </div>

          <div v-if="editingId === note.id" class="mt-2" @keydown.esc.stop="cancelEdit">
            <v-textarea
              v-model="editText"
              :counter="MAX_LENGTH"
              :maxlength="MAX_LENGTH"
              :disabled="savingEdit"
              auto-grow
              rows="3"
              outlined
              autofocus
              hide-details="auto"
            />
            <v-alert v-if="editError" type="error" dense text class="mt-2 mb-0">{{ editError }}</v-alert>
            <div class="d-flex justify-end mt-2">
              <v-btn text :disabled="savingEdit" @click="cancelEdit">Cancelar</v-btn>
              <v-btn color="primary" depressed :loading="savingEdit" :disabled="!editText.trim()" @click="saveEdit">
                Guardar
              </v-btn>
            </div>
          </div>

          <template v-else>
            <!-- El backend ya saneó este HTML (spec 133, FR-005). -->
            <div
              class="org-notes__content"
              :class="{ 'org-notes__content--collapsed': isLong(note) && !expanded[note.id] }"
              v-html="note.content"
            />
            <v-btn v-if="isLong(note)" x-small text color="primary" class="px-0" @click="toggle(note.id)">
              {{ expanded[note.id] ? 'Ver menos' : 'Ver más' }}
            </v-btn>
          </template>
        </div>

        <div class="mt-3">
          <a v-if="hasMore && pipedriveUrl" :href="pipedriveUrl" target="_blank" rel="noopener">
            Ver todas en Pipedrive
          </a>
          <p v-else-if="notes.length" class="org-notes__hint mb-0">Para borrar una nota, hazlo en Pipedrive.</p>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import ClientOrgChartsService from '../services/client-org-charts.service';
import { errorMessage } from '../utils/org-chart.utils';

const MAX_LENGTH = 5000;
// Sobre esto la nota se muestra cortada con "Ver más".
const LONG_NOTE_CHARS = 600;
const DATE_FORMAT = new Intl.DateTimeFormat('es-CL', {
  timeZone: 'America/Santiago',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
});

export default {
  name: 'OrgChartNotes',
  props: {
    pipedriveOrgId: { type: [Number, String], required: true }
  },
  data() {
    return {
      MAX_LENGTH,
      notes: [],
      hasMore: false,
      pipedriveUrl: '',
      loading: false,
      loadError: '',
      draft: '',
      saving: false,
      saveError: '',
      editingId: null,
      editText: '',
      savingEdit: false,
      editError: '',
      expanded: {}
    };
  },
  computed: {
    canSave() {
      return this.draft.trim().length > 0 && !this.saving;
    }
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = '';
      const res = await ClientOrgChartsService.getOrganizationNotes(this.pipedriveOrgId);
      this.loading = false;
      if (res.status !== 'ok') {
        this.loadError = errorMessage(res, 'No pudimos cargar las notas desde Pipedrive.');
        return;
      }
      this.notes = res.data || [];
      this.hasMore = Boolean(res.meta && res.meta.hasMore);
      this.pipedriveUrl = (res.meta && res.meta.pipedriveUrl) || '';
    },
    async save() {
      if (!this.canSave) return;
      this.saving = true;
      this.saveError = '';
      const res = await ClientOrgChartsService.addOrganizationNote(this.pipedriveOrgId, this.draft.trim());
      this.saving = false;
      if (res.status !== 'ok') {
        this.saveError = errorMessage(res, 'No pudimos guardar la nota en Pipedrive. Intenta de nuevo.');
        return;
      }
      this.notes = [res.data, ...this.notes];
      this.draft = '';
    },
    startEdit(note) {
      this.editingId = note.id;
      this.editText = note.text || '';
      this.editError = '';
    },
    cancelEdit() {
      if (this.savingEdit) return;
      this.editingId = null;
      this.editText = '';
      this.editError = '';
    },
    async saveEdit() {
      const text = this.editText.trim();
      if (!text) return;
      this.savingEdit = true;
      this.editError = '';
      const res = await ClientOrgChartsService.editOrganizationNote(this.pipedriveOrgId, this.editingId, text);
      this.savingEdit = false;
      if (res.status !== 'ok') {
        this.editError = errorMessage(res, 'No pudimos guardar el cambio en Pipedrive. Intenta de nuevo.');
        return;
      }
      this.notes = this.notes.map(note => (note.id === res.data.id ? res.data : note));
      this.editingId = null;
      this.editText = '';
    },
    isLong(note) {
      return (note.content || '').length > LONG_NOTE_CHARS;
    },
    toggle(noteId) {
      this.$set(this.expanded, noteId, !this.expanded[noteId]);
    },
    formatDate(iso) {
      return iso ? DATE_FORMAT.format(new Date(iso)) : '';
    }
  }
};
</script>

<style scoped>
.org-notes__note {
  padding: 12px 0;
  border-bottom: 1px solid var(--pw-divider);
}
.org-notes__note:last-of-type {
  border-bottom: none;
}
.org-notes__meta {
  font-size: 13px;
  color: var(--pw-text-muted);
}
.org-notes__edited {
  font-style: italic;
}
.org-notes__content {
  margin-top: 6px;
  white-space: normal;
  overflow-wrap: anywhere;
}
.org-notes__content >>> p {
  margin-bottom: 6px;
}
.org-notes__content--collapsed {
  max-height: 10.5em;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(180deg, #000 70%, transparent);
  mask-image: linear-gradient(180deg, #000 70%, transparent);
}
.org-notes__empty,
.org-notes__hint {
  color: var(--pw-text-muted);
}
</style>
